import { Alert, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  HiOutlineBadgeCheck,
  HiOutlineBriefcase,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
  HiOutlineHome,
  HiOutlineInformationCircle,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineUserGroup,
  HiOutlineViewGrid,
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import {
  ClosureDTO,
  FeeArrangementDTO,
  ICandidate,
  IJob,
  createClosure,
  findByName,
  getCurrencyList,
  getFeeArrangementById,
  getJobById,
  getPlacementStatusList,
} from 'wisestep-api';
import {
  formatLocation,
  WsButton,
  WsColors,
  WsDatePicker,
  WsDrawer,
  WsIconLocation,
  WsIconText,
  WsTypoVariants,
} from 'wisestep-base';

import { useFetch } from '../../../../../hooks/useFetch';
import { failed, success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { closurePayload } from '../../../../closures/helper/payload';

export interface IComponentProps {
  open: boolean;
  onClose: () => void;
  candidate: ICandidate | null;
}

interface ISubmissionDetail {
  icon?: React.ReactElement;
  labelName: string | number;
  popOverText?: string | number;
  cursor?: 'pointer' | 'text';
}

interface ITeamRecruiterDTO {
  text: string | null | undefined;
  email: string | null | undefined;
  id: string | null | undefined;
  name: string | null | undefined;
}

const ConvertPlacement = ({ open, onClose, candidate }: IComponentProps) => {
  const [currencies] = useFetch(getCurrencyList);
  const [submissionDetails, setSubmissionDetails] = useState<ISubmissionDetail[]>([]);
  const [selectedFA, setSelectedFA] = useState<FeeArrangementDTO>();
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [showFeeArrError, setShowFeeArrError] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<IJob>();
  const [placementStatusList] = useFetch(getPlacementStatusList);
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const methods = useForm<ClosureDTO>({
    defaultValues: { ...closurePayload },
  });

  const calculateComissionValue = (fa: FeeArrangementDTO): string => {
    let commissionValueStr = '';
    let value = 0;
    if (!fa.feeType || fa.feeType === 'FIXED') {
      value = fa?.fee;
      commissionValueStr = `${fa?.currency.symbol ?? ''} ${value ?? ''}`;
    } else {
      value = ((candidate?.expectedBaseSalary ?? 0) * fa.fee) / 100;
      commissionValueStr = `${candidate?.expectedSalaryCurrency ?? ''} ${value}`;
    }
    methods.setValue('commissionValue', value);
    return commissionValueStr;
  };

  const calculateEmpGuaranteeDays = (fa: FeeArrangementDTO): string => {
    let empDays = 0;
    if (fa.employmentGuarantee) {
      empDays =
        ({ DAYS: 1, WEEKS: 7, MONTHS: 30 }[fa?.empGuaranteePeriodUnit] ?? 0) *
        fa.employmentGuarantee;
      methods.setValue('employmentGuaranteeDays', empDays);
    }
    return empDays === 0 ? 'NOT AVAILABLE' : `${empDays} Days`;
  };

  useEffect(() => {
    const getSubDetails = (fa: FeeArrangementDTO | null, job: IJob | null): ISubmissionDetail[] => {
      const subDetails: ISubmissionDetail[] = [];
      subDetails.push({
        labelName: candidate?.email as string,
        popOverText: 'Candidate Email',
        icon: <HiOutlineMail color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: candidate?.phoneNumber as string,
        popOverText: 'Candidate Phone',
        icon: <HiOutlinePhone color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: candidate?.companyName as string,
        popOverText: 'Client Name',
        icon: <HiOutlineHome color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: candidate?.jobClientRecruiterName as string,
        popOverText: 'Client SPOC',
        icon: <HiOutlineUser color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: candidate?.team?.name as string,
        popOverText: 'Team',
        icon: <HiOutlineUserGroup color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: candidate?.jobAccountManagerName as string,
        popOverText: 'Account Manager',
        icon: <HiOutlineUser color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: `${candidate?.expectedSalaryCurrency ?? ''} ${
          candidate?.expectedBaseSalary ?? ''
        } ${candidate?.expectedBaseSalaryTimeSpan ?? ''}`,
        popOverText: 'Compensation',
        icon: <HiOutlineCurrencyDollar color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: `joiningDate`,
      });
      subDetails.push({
        labelName: `${
          fa
            ? fa.contractType + ' ' + fa.fee + (fa.feeType === 'PERCENTAGE' ? '%' : '')
            : 'NOT AVAILABLE'
        }`,
        popOverText: 'Rate',
        icon: <HiOutlineInformationCircle color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: fa ? calculateComissionValue(fa) : 'NOT_AVAILABLE',
        popOverText: 'Commission Value',
        icon: <HiOutlineCurrencyDollar color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: fa ? calculateEmpGuaranteeDays(fa) : 'NOT_AVAILABLE',
        popOverText: 'Employment Guarantee Days',
        icon: <HiOutlineClock color={WsColors.TertiaryShades700} />,
      });
      subDetails.push({
        labelName: job?.businessUnitName ?? 'NOT_AVAILABLE',
        popOverText: 'Business Unit',
        icon: <HiOutlineViewGrid color={WsColors.TertiaryShades700} />,
      });
      return subDetails;
    };

    const getClientData = async () => {
      if (candidate?.companyName) {
        const resp = await findByName(candidate?.companyName);
        if (resp.data) {
          const client = resp.data.data;
          setSelectedClientId(client.id);
        }
      }
    };

    const getJobData = async () => {
      if (candidate?.jobId) {
        const response = await getJobById(candidate?.jobId);
        if (response) {
          const job: IJob = response.data;
          setSelectedJob(job);
          if (job.feeArrangementId) {
            const faResponse = await getFeeArrangementById(job.feeArrangementId);
            setSelectedFA(faResponse.data);
            setSubmissionDetails(getSubDetails(faResponse.data, job));
          } else {
            setSubmissionDetails(getSubDetails(null, job));
            setShowFeeArrError(true);
          }
          getClientData();
        }
      }
    };

    getJobData();
  }, [candidate]);

  const createPlacement = async (closureDto: ClosureDTO) => {
    closureDto.candidateLocation = candidate?.currentLocation ?? null;
    closureDto.candidateName = candidate?.name as string;
    closureDto.candidateEmail = candidate?.email as string;
    if (candidate?.phoneNumber) {
      closureDto.candidateContact = candidate?.phoneNumber as string;
    }
    if (selectedJob?.businessUnit) {
      closureDto.businessUnitId = selectedJob?.businessUnit ?? null;
      closureDto.businessUnitName = selectedJob?.businessUnitName ?? null;
    }
    closureDto.feeArrangementId = selectedFA?.id ?? '';
    closureDto.feeArrangementAmount = selectedFA?.fee ?? 0;
    closureDto.feeType = selectedFA?.feeType ?? '';
    closureDto.arrangementType = 'SELECTED';
    closureDto.period = 'MONTHLY';
    closureDto.positionId = candidate?.jobId ?? '';
    closureDto.positionTitle = candidate?.mappedJobTitle ?? '';
    closureDto.compensationAmount = candidate?.expectedBaseSalary ?? 0;
    const defaultStatus = placementStatusList.find((s) => s.placementStatusName === 'Placed');
    if (defaultStatus) {
      closureDto.placementStatusId = defaultStatus.id;
    }
    if (candidate?.jobAccountManagerEmail) {
      const accountManger: ITeamRecruiterDTO = {
        email: candidate?.jobAccountManagerEmail,
        name: candidate?.jobAccountManagerName,
        text: candidate?.jobAccountManagerName,
        id: undefined,
      };
      closureDto.accountManager = JSON.stringify(accountManger);
    }
    if (candidate?.jobClientRecruiterEmail) {
      closureDto.clientRecruiterEmail = candidate?.jobClientRecruiterEmail as string;
    }
    closureDto.clientId = selectedClientId;
    closureDto.clientName = candidate?.companyName ?? '';
    closureDto.submissionId = candidate?.id ?? '';
    if (candidate?.team) {
      const team: ITeamRecruiterDTO = {
        email: candidate.team.ownerEmail,
        name: candidate.team.name,
        text: candidate.team.name,
        id: candidate.team.id,
      };
      closureDto.team = JSON.stringify(team);
    }

    const currency = currencies.find((c) => c.symbol === candidate?.currentSalaryCurrency);
    if (currency) {
      closureDto.currencyId = currency.id as string;
    }
    closureDto.currencySymbol = currency?.symbol ?? candidate?.currentSalaryCurrency;
    if (closureDto.joiningDate) {
      if (selectedFA) {
        const date = new Date(closureDto.joiningDate);
        const empDays =
          ({ DAYS: 1, WEEKS: 7, MONTHS: 30 }[selectedFA?.empGuaranteePeriodUnit] ?? 0) *
          selectedFA.employmentGuarantee;
        date.setDate(date.getDate() + empDays);
        closureDto.employmentGuaranteeEndDate = date;
      }
    }

    try {
      const resp = await createClosure(closureDto);
      if (resp) {
        setNotification(success('Placement created successfully'));
        navigate('/placements/' + resp?.data?.value, {
          state: { placementType: 'PERMANENT' },
        });
      }
    } catch (err) {
      setNotification(failed('Error while converting to placement'));
    }
  };

  return (
    <WsDrawer
      open={open}
      onClose={onClose}
      size="small"
      title="Convert submission to Permanent Placement"
    >
      {candidate && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(createPlacement)}>
            <Grid container pl={2} py={1}>
              <Grid item p={1} sm={12} flexDirection="row">
                <Typography variant="h4">{candidate.name}</Typography>
              </Grid>
              {candidate.currentLocation && (
                <Grid item pl={2} pb={2} sm={12}>
                  <WsIconText
                    icon={<WsIconLocation color={WsColors.TertiaryShades700} />}
                    text={formatLocation(candidate.currentLocation)}
                    opacity={true}
                    textCursor="pointer"
                    popOverText="Candidate Location"
                    textSize={WsTypoVariants.BODY1}
                  />
                </Grid>
              )}
              <Grid item p={2} sm={6}>
                <WsIconText
                  icon={<HiOutlineBriefcase color={WsColors.TertiaryShades700} />}
                  text={`${candidate.mappedJobTitle} ${candidate.jobCode}` ?? ''}
                  opacity={true}
                  textCursor="pointer"
                  popOverText="Job Title"
                  textSize={WsTypoVariants.BODY1}
                />
              </Grid>
              <Grid item p={2} sm={6}>
                <WsIconText
                  icon={<HiOutlineBadgeCheck color={WsColors.TertiaryShades700} />}
                  text={'Permanent'}
                  opacity={true}
                  textCursor="pointer"
                  popOverText="Job Type"
                  textSize={WsTypoVariants.BODY1}
                />
              </Grid>
              {submissionDetails?.map((detailsObj, index) => (
                <Grid item p={2} sm={6} key={index}>
                  {detailsObj.labelName !== 'joiningDate' && (
                    <WsIconText
                      icon={detailsObj.icon ?? <></>}
                      text={detailsObj.labelName ?? 'NOT AVAILABLE'}
                      opacity={true}
                      textCursor="pointer"
                      popOverText={`${detailsObj.popOverText}`}
                      textSize={WsTypoVariants.BODY1}
                    />
                  )}
                  {detailsObj.labelName === 'joiningDate' && (
                    <WsDatePicker
                      name="joiningDate"
                      label="Select Joining Date"
                      required={true}
                      size="small"
                    />
                  )}
                </Grid>
              ))}
              {showFeeArrError && (
                <Grid item p={1} sm={12}>
                  <Alert severity="warning">Fee Arrangement is not mapped with this Job.</Alert>
                </Grid>
              )}
              <Grid item sm={20} sx={{ bottom: 1, right: 1, position: 'fixed' }}>
                <Stack p={1} spacing={2} direction="row" justifyContent="right">
                  <WsButton variant="outlined" size="large" onClick={() => onClose()}>
                    Cancel
                  </WsButton>
                  <WsButton variant="contained" size="large" type="submit">
                    Create Placement
                  </WsButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      )}
    </WsDrawer>
  );
};

export default ConvertPlacement;
